import { useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import SideBarTool from "@/commons/components/side-bar-tool";
import { SideBarBtnType } from "@/commons/enums/side-bar-btn-type";
import { getStoreys } from "@/api/building/storey";
import { Storey } from "@/commons/interface/building/storey";
import { StoreyStatus } from "@/commons/context/storey-store";
import { StoreyContext } from "@/App";
import { getComponents } from "@/api/spec-common/component";
import { ComponentType } from "@/commons/enums/component-type";
import { BlockT, LineType, ModelBase } from "pytha";
import { graphicStore } from "@/commons/store/graphic-store";
import { BUILDING_SINGAL, BUILDING_SUB_CONTROL, ComponentT, DefaultStoreyUuid, FireHydrantT, GroupType } from "tnbimbase";
import { onActiveSubControl } from "@/commons/utils/graphic-method";
import { Vector3 } from "three";
import { createPrimitive, deletePrimitivesInGraphic, findPrimitivesInGraphic } from "@/api/geometry/primitive";
import { extractFirstNumber } from "@/commons/utils/string-method";
import { Modal, message } from "antd";
import TnModal from "@/commons/widget/base-modal";
import { SecondaryDropdownBox } from "@/commons/components/secondary-dropdown-box";
import { findLayersInProjectUuid, saveLayer } from "@/api/geometry/layer";
import { Layer } from "@/commons/interface/geometry/layer";
import { LayerType } from "@/commons/enums/layer-type";
import genUUID from "@/commons/utils/gen-uuid";


export function FireHydrantPanel() {

    const detailRef = useRef(null);

    const { projectUuid, modelUuid } = useParams();
    const storeyContext = useContext<StoreyStatus>(StoreyContext);
    const [fireEquipmentEntity, setFireEquipmentEntity] = useState<ModelBase>(); //消防设备组件
    const [storeyList, setStoreyList] = useState<Storey[]>([]);
    const [init, setInit] = useState<boolean>(false);  //防止第一次调用onSelectedChanged的信号时导致面板为空

    const [selectedUuid, setSelectedUuid] = useState<string>("");

    const [maxNumber, setMaxNumber] = useState<number>(0);  //最大设备编号

    const onChangeSelectedUuid = (v: string) => {
        setSelectedUuid(v);
    }

    const generateData = () => {
        if (!projectUuid || !modelUuid) {
            storeyContext.setStoreys([]);
            return;
        }
        getStoreys(projectUuid, modelUuid).then(res => {
            //为保持全局一致性，不直接从storeyContext中拿，每次都查询后更新
            if (res.status === 200) {
                let data: Storey[] = res.data || [];
                let bottomElevations: number[] = [];

                //按照楼层标高从高到低排序
                data.sort((a, b) => {
                    return - a.bottomElevation + b.bottomElevation;
                });
                data.forEach(item => {
                    bottomElevations.push(item.bottomElevation);
                    item.childList = [];
                });
                storeyContext.setStoreys(data);

                findPrimitivesInGraphic(projectUuid, { type: "FireHydrantT", comType: ComponentType.FIRE_HYDRANT, modelUuids: [modelUuid] }).then(res2 => {
                    if (res2.status === 200) {
                        let primitives = res2.data || [];
                        let tmpMaxNumber = 0;

                        primitives?.forEach(element => {
                            let z = element.properties.position.z / 1000;
                            element.name = element.properties.name;
                            element.isSelected = false;
                            let tmpNumber = Number(extractFirstNumber(element.name));
                            tmpMaxNumber = tmpMaxNumber > tmpNumber ? tmpMaxNumber : tmpNumber;
                            let added = false;
                            for (let i = 0; i < bottomElevations.length; i++) {
                                if (z >= bottomElevations[i]) {
                                    data[i].childList.push(element);
                                    added = true;
                                    break;
                                }
                            }
                            if (!added && bottomElevations.length > 0) {
                                //如果标高没有被归类为前面的项，说明低于最低楼层的标高，直接归为最低楼层
                                data[bottomElevations.length - 1].childList.push(element);
                            }
                        });
                        data.forEach(item => {
                            item.nameSuffix = `(${item.bottomElevation}m)\t(已建${item.childList.length}个消火栓)`
                        });
                        setMaxNumber(tmpMaxNumber);
                        setStoreyList(data);
                    }

                })


            }
        }).catch(err => {
            console.log('err', err)
        })

        getComponents({ projectUuid: projectUuid, type: ComponentType.FIRE_HYDRANT }).then(res => {
            if (res.status == 200) {
                let components: any[] = res.data || [];
                if (components.length > 0) {
                    let firstComponent = components[0];
                    let entity: ModelBase = new FireHydrantT().fromJson(firstComponent);
                    setFireEquipmentEntity(entity);
                }
            }
        })
    }

    /**
     * 项目初始化的时候，生成数据
     */
    useEffect(() => {
        generateData();
    }, [projectUuid, modelUuid])

    const onEquipmentPositionUpdate = (entities: ModelBase[], needDeleted: boolean = false) => {
        let equipments = entities?.filter(entity => entity.type === "FireHydrantT");
        let equipmentsUuidSet = new Set<string>(equipments.map(item => item.uuid)); //存储被修改后的图元的uuid
        if (equipments.length > 0) {
            let tmpStoreyList = [...storeyList];

            let bottomElevations: number[] = tmpStoreyList.map(storey => storey.bottomElevation);

            //从列表中移除被修改的图元
            tmpStoreyList.forEach(storey => {
                storey.childList = storey.childList.filter(item => !(equipmentsUuidSet.has(item.uuid)));  //filter操作创建新数组，牺牲空间换时间
            })

            if (!needDeleted) {//如果需要被删除，就不重新添加进去了
                equipments.forEach((item: FireHydrantT) => {
                    let z = item.position.z / 1000;
                    let added = false;
                    for (let i = 0; i < bottomElevations.length; i++) {

                        if (z >= bottomElevations[i]) {
                            let json = item.toJson() as any;
                            tmpStoreyList[i].childList.push({ ...json, isSelected: item.isSelected });
                            added = true;
                            break;
                        }
                    }
                    if (!added && tmpStoreyList.length > 0) {
                        //如果标高没有被归类为前面的项，说明低于最低楼层的标高，直接归为最低楼层
                        let json = item.toJson() as any;
                        tmpStoreyList[bottomElevations.length - 1].childList.push({ ...json, isSelected: item.isSelected });
                    }
                })
                tmpStoreyList.forEach(item => {
                    item.nameSuffix = `(${item.bottomElevation}m)\t(已建${item.childList.length}个消火栓)`
                });
            } else {
                //如果是删除模式，只更新每层的个数
                tmpStoreyList.forEach(item => {
                    item.nameSuffix = `(${item.bottomElevation}m)\t(已建${item.childList.length}个消火栓)`
                });
            }
            setStoreyList(tmpStoreyList);
        }
    }

    /**
     * 设备位置修改后的回调，会修改设备对应楼层
     */
    useEffect(() => {
        if (graphicStore.extraContext.getCurrentViewEditor()) {
            graphicStore.extraContext.getCurrentViewEditor().listeners.signals.onEntitiesUpdatePosition.add(onEquipmentPositionUpdate)
        } else {
            //防止面板被提前打开
            setTimeout(() => {
                if (graphicStore.extraContext.getCurrentViewEditor()) {
                    graphicStore.extraContext.getCurrentViewEditor().listeners.signals.onEntitiesUpdatePosition.add(onEquipmentPositionUpdate)
                }
            }, 3000);
        }

        return () => {
            if (graphicStore.extraContext.getCurrentViewEditor()) {
                graphicStore.extraContext.getCurrentViewEditor().listeners.signals.onEntitiesUpdatePosition.remove(onEquipmentPositionUpdate)
            }
        }
    }, [onEquipmentPositionUpdate])


    const onQuicklyAssembleBlockFinish = (entity: ModelBase) => {
        let tmpNumber = Number(extractFirstNumber(entity.name));
        if (tmpNumber > maxNumber) {
            setMaxNumber(tmpNumber);
            graphicStore.extraContext.getCurrentViewEditor().listeners.getSignal(BUILDING_SINGAL.onQuicklyAssembleBlockChangeName).dispatch(`消火栓${tmpNumber + 1}`)
        }
        let bottomElevations = storeyList.map(item => item.bottomElevation);
        let tmpStoreyList = [...storeyList];
        let added = false;
        let z = (entity as ComponentT).position.z / 1000;
        for (let i = 0; i < bottomElevations.length; i++) {

            if (z >= bottomElevations[i]) {
                let json = (entity as ComponentT).toJson() as any;
                tmpStoreyList[i].childList.push({ ...json, isSelected: entity.isSelected });
                added = true;
                break;
            }
        }
        if (!added && tmpStoreyList.length > 0) {
            //如果标高没有被归类为前面的项，说明低于最低楼层的标高，直接归为最低楼层
            let json = (entity as ComponentT).toJson() as any;
            tmpStoreyList[bottomElevations.length - 1].childList.push({ ...json, isSelected: entity.isSelected });
        }
        tmpStoreyList.forEach(item => {
            item.nameSuffix = `(${item.bottomElevation}m)\t(已建${item.childList.length}个消火栓)`
        });
        setStoreyList(tmpStoreyList);
    }


    /**
     * 设备在图形区添加完毕后，在面板上的回调（多显示一行）
     */
    useEffect(() => {
        if (graphicStore.extraContext.getCurrentViewEditor()) {
            graphicStore.extraContext.getCurrentViewEditor().listeners.getSignal(BUILDING_SINGAL.onQuicklyAssembleBlockFinish).add(onQuicklyAssembleBlockFinish)
        } else {
            //防止面板被提前打开
            setTimeout(() => {
                if (graphicStore.extraContext.getCurrentViewEditor()) {
                    graphicStore.extraContext.getCurrentViewEditor().listeners.getSignal(BUILDING_SINGAL.onQuicklyAssembleBlockFinish).add(onQuicklyAssembleBlockFinish)
                }
            }, 3000);
        }

        return () => {
            if (graphicStore.extraContext.getCurrentViewEditor()) {
                graphicStore.extraContext.getCurrentViewEditor().listeners.getSignal(BUILDING_SINGAL.onQuicklyAssembleBlockFinish).remove(onQuicklyAssembleBlockFinish)
            }
        }
    }, [onQuicklyAssembleBlockFinish])


    const onSelectChange = (entities: ModelBase[]) => {
        if (!init) {
            setInit(true);
            return;
        }
        let equipments = entities.filter(item => item instanceof FireHydrantT) as FireHydrantT[];
        let equipmentDict: { [key: string]: boolean } = {};
        equipments.forEach(item => equipmentDict[item.uuid] = item.isSelected);
        let tmpStoreyList = [...storeyList];
        for (let i = 0; i < tmpStoreyList.length; i++) {
            let storey = tmpStoreyList[i];
            for (let j = 0; j < storey.childList.length; j++) {
                if (equipmentDict.hasOwnProperty(storey.childList[j].uuid)) {
                    storey.childList[j].isSelected = equipmentDict[storey.childList[j].uuid];
                }
            }
        }
        setStoreyList(tmpStoreyList);
    }

    /**
     * 改变图形区选中后，列表的回调
     */
    useEffect(() => {
        if (graphicStore.extraContext.getCurrentViewEditor()) {
            graphicStore.extraContext.getCurrentViewEditor().listeners.signals.onSelectChanged.add(onSelectChange)
        } else {
            //防止面板被提前打开
            setTimeout(() => {
                if (graphicStore.extraContext.getCurrentViewEditor()) {
                    graphicStore.extraContext.getCurrentViewEditor().listeners.signals.onSelectChanged.add(onSelectChange)
                }
            }, 3000);
        }

        return () => {
            if (graphicStore.extraContext.getCurrentViewEditor()) {
                graphicStore.extraContext.getCurrentViewEditor().listeners.signals.onSelectChanged.remove(onSelectChange)
            }
        }
    }, [onSelectChange])



    /**
     * 布置
     */
    const onAddClick = () => {
        if (!fireEquipmentEntity) return;
        let block = fireEquipmentEntity.clone();
        let box = block.getBoundingBox();
        let centerPosition = new Vector3();
        box.getCenter(centerPosition);
        centerPosition.setZ(box.min.z);

        findLayersInProjectUuid(projectUuid, modelUuid).then(res => {
            let layers: Layer[] = res.data || [];
            let componentLayer = layers?.find(item => item?.type === LayerType.FIRE_HYDRANT);
            if (!!componentLayer) {
                // 存在一个图层为构件图层，绘制的图元都在该图层中
                onActiveSubControl(BUILDING_SUB_CONTROL.QUICKLYASSEMBLEBLOCK.name);
                graphicStore.extraContext.getCurrentViewEditor()?.listeners.getSignal(BUILDING_SINGAL.onQuicklyAssembleBlock)
                    .dispatch(fireEquipmentEntity, centerPosition, componentLayer.uuid, `消火栓${maxNumber + 1}`, [{ key: GroupType.STOREY, value: DefaultStoreyUuid.DYNAMIC }]);
            } else {
                // 不存在一个图层为构件图层，新建一个图层作为构件图层，绘制的图元都在该图层中
                let uuid = genUUID();
                let newLayer: Layer = {
                    uuid: uuid,
                    name: "消火栓",
                    color: "#000000",
                    lineWidth: 0.25,
                    lineType: LineType.BASE_LINE,
                    lock: false,
                    hidden: false,
                    projectUuid: projectUuid,
                    originLayer: false,
                    type: LayerType.FIRE_HYDRANT,
                };
                saveLayer(newLayer, projectUuid, modelUuid).then(res => {
                    if (res.status === 200) {
                        graphicStore.context.layerContext.addLayer(newLayer);
                        onActiveSubControl(BUILDING_SUB_CONTROL.QUICKLYASSEMBLEBLOCK.name);
                        graphicStore.extraContext.getCurrentViewEditor()?.listeners.getSignal(BUILDING_SINGAL.onQuicklyAssembleBlock)
                            .dispatch(fireEquipmentEntity, centerPosition, uuid, `消火栓${maxNumber + 1}`, [{ key: GroupType.STOREY, value: DefaultStoreyUuid.DYNAMIC }]);
                    }
                }).catch(err => {
                    message.error("无法置为构件图层");
                    console.log(err);
                })
            }
        })



    }

    /**
     * 删除
     */
    const onDeleteClick = () => {
        let selectedUuids = [];
        let tmpStoreyList = [...storeyList];
        tmpStoreyList.forEach(storey => {
            storey.childList.forEach(item => {
                if (item.isSelected) {
                    selectedUuids.push(item.uuid);
                }
            })
        })
        if (selectedUuids?.length === 0) {
            message.info("未选中消火栓");
            return;
        }
        tmpStoreyList.forEach(storey => {
            storey.childList = storey.childList.filter(item => !item.isSelected);
            storey.nameSuffix = `(${storey.bottomElevation}m)\t(已建${storey.childList?.length || 0}个消火栓)`
        })

        TnModal().deleteModal({
            closable: false,
            onOk() {
                deletePrimitivesInGraphic(selectedUuids, projectUuid).then(res => {
                    if (res.status === 200) {
                        message.success("删除成功");
                        setStoreyList(tmpStoreyList);
                        let editor = graphicStore.extraContext.getCurrentViewEditor();
                        if (editor) {
                            editor.removeObjectByUuids(selectedUuids);

                        }
                    }
                }).catch(err => {
                    console.log('err', err)
                }).finally(() => {
                    Modal.destroyAll();
                })
            },
            onCancel() {
                graphicStore.extraContext.getCurrentViewEditor()?.listeners.signals.emptySelectedEntities.dispatch();
                graphicStore.extraContext.getCurrentViewEditor()?.listeners.signals.needRender.dispatch('redraw');
                Modal.destroyAll();
            },
        })

    }


    /**
     * 更新选中状态
     */
    const setSelectedEntity = (uuid: string, selected: boolean) => {
        let tmpStoreyList = [...storeyList];
        for (let i = 0; i < tmpStoreyList.length; i++) {
            let storey = tmpStoreyList[i];
            let has = false;
            for (let j = 0; j < storey.childList.length; j++) {
                if (storey.childList[j].uuid === uuid) {
                    storey.childList[j].isSelected = selected;
                    has = true;
                    break;
                }
            }
            if (has) {
                break;
            }
        }
        setStoreyList(tmpStoreyList);
    }


    /**
     * 更新名称
     */
    const updateName = (uuid: string, name: string) => {
        let tmpStoreyList = [...storeyList];
        for (let i = 0; i < tmpStoreyList.length; i++) {
            let storey = tmpStoreyList[i];
            let has = false;
            for (let j = 0; j < storey.childList.length; j++) {
                if (storey.childList[j].uuid === uuid) {
                    storey.childList[j].name = name;
                    if ((storey.childList[j] as any).toJson) {
                        createPrimitive((storey.childList[j] as any).toJson());
                    } else {
                        if ((storey.childList[j] as any).properties) {
                            let data = (storey.childList[j] as any).properties
                            data.name = name
                            createPrimitive(data);
                        } else {
                            let data = { ...storey.childList[j] };
                            delete data.isSelected;
                            createPrimitive(data);
                        }
                    }
                    has = true;
                    break;
                }
            }
            if (has) {
                break;
            }
        }
        setStoreyList(tmpStoreyList);
    }






    return (
        <div className="side-bar-main patrol-point-container">
            <SideBarTool
                activeBtnList={[SideBarBtnType.ADD, SideBarBtnType.DELETE]}
                onAddBtnClick={onAddClick}
                onDeleteBtnClick={onDeleteClick}
            />
            <div className="body">
                <div className="title">
                    消火栓
                </div>
                <div className="detail" ref={detailRef}>
                    <SecondaryDropdownBox
                        datas={storeyList}
                        selectedUuid={selectedUuid}
                        onChangeSelectedUuid={onChangeSelectedUuid}
                        setSelectedEntity={setSelectedEntity}
                        updateName={updateName}
                        type="fireHydrant"
                        title="消火栓数据集"
                    />
                </div>
            </div >
        </div >
    )

}